import { action, computed, makeObservable, observable } from 'mobx';

import { ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import { CallWaitingRoom } from '@gem/call/CallStore/CallWaitingRoom/CallWaitingRoom';
import { MeStore } from '@gem/call/CallStore/Me/Me.store';
import { MediaDevices } from '@gem/call/CallStore/MediaDevices/MediaDevices';
import { Opponent } from '@gem/call/CallStore/Opponent/Opponent';
import { CallProviderType } from '@gem/call/Model/callModel.types';
import { PermissionsTypes } from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { MAX_COUNT_BROADCASTERS } from '@gem/call/constants';

import { CallConnectionQuality } from '../CallProviders/callProviders.constants';
import {
  CallMediaPermissionValue,
  ICallAvatar,
  ICallConstructorParams,
  ICallLimits,
} from './Call.types';
import { CallPermissions } from './CallPermissions/CallPermissions';
import Events from './Events/Events';
import { Members } from './Members/Members';
import { Opponents } from './Opponents/Opponents';
import { OpponentsPositions } from './OpponentsPositions/OpponentsPositions';
import { UiFeatures } from './UiFeatures/UiFeatures';
import { ViewState } from './ViewState/ViewState';

/* workAround, до согласования логики макс. кол. участников которые могут подключиться к звонку */
export const MAX_COUNT_PARTICIPANTS = 1000;

export class Call {
  @observable callLimits: ICallLimits = {
    maxParticipants: MAX_COUNT_PARTICIPANTS,
    maxParticipantsReminder: null,
    maxDuration: null,
    maxDurationReminder: null,
    videoQualitySettings: null,
  };

  @observable approxDurationMs = 0;
  @observable duration = 0;
  @observable uiFeatures = new UiFeatures();
  @observable isGuestsAllowed = true;
  @observable mediaDevices: MediaDevices;
  @observable meStore: MeStore;
  @observable members = new Members(this);
  @observable opponents = new Opponents(this);
  @observable opponentsPositions = new OpponentsPositions(this);
  @observable permissions = new CallPermissions(this);
  @observable viewState = new ViewState(this);
  @observable waitingRoom = new CallWaitingRoom(this);
  providerType: CallProviderType;
  @observable groupId: string | null = null;
  @observable isStarted = false;
  @observable maxBroadcasters = MAX_COUNT_BROADCASTERS;
  @observable channelId: string | null = null;
  @observable initiatorId: string | null = null;
  @observable providerUid: ProviderUid | null = null;
  @observable videoPermission = CallMediaPermissionValue.Initial;
  @observable audioPermission = CallMediaPermissionValue.Initial;
  @observable connectionQuality = CallConnectionQuality.Unknown;
  @observable avatar: ICallAvatar = {
    title: null,
    url: null,
    colorIndex: null,
  };

  @observable activeOpponentUid: ProviderUid | null = null;
  @observable startTs: number | null = null;

  @observable isBusy = false;
  @observable isNoAnswer = false;

  @observable recordingId: string | null = null;
  @observable isUnauthorizedCall = false;
  @observable inviteLink = '';
  @observable isCallOwnerOrAdmin = false;
  @observable initialMeOpponentAvatar: ICallAvatar | null = null;
  isDisposing = false;
  sortIndex = 1;
  events: Events;

  constructor(params: ICallConstructorParams) {
    makeObservable(this);
    const { groupId, channelId, providerType, isUnauthorizedCall = false, myId } = params;
    this.meStore = new MeStore(myId);
    this.providerType = providerType;
    this.groupId = groupId || null;
    this.channelId = channelId || null;

    this.isUnauthorizedCall = isUnauthorizedCall;
    this.events = new Events();
    this.mediaDevices = new MediaDevices(params.initialDevicesIds);

    if (params.mediaPermissions) {
      this.audioPermission = params.mediaPermissions.audio;
      this.videoPermission = params.mediaPermissions.video;
    }
    // TODO: выпилить и переделать на инишиал MeMember
    this.initialMeOpponentAvatar = params.initialMeOpponentAvatar || null;
  }

  @computed
  get isMeInitiator(): boolean {
    return !this.channelId || this.initiatorId === this.meStore.id;
  }

  @computed
  get isAnswered(): boolean {
    return Boolean(this.startTs);
  }

  @computed
  get isJoining(): boolean {
    return !this.isMeInitiator && !this.isAnswered && !this.isBusy && !this.isNoAnswer;
  }

  @computed
  get isCalling(): boolean {
    return this.isMeInitiator && !this.isAnswered;
  }

  @computed
  get isVideoCall(): boolean {
    return Boolean(this.opponents.opponentsWithVideo.length);
  }

  @action
  setStarted(isStarted: boolean): void {
    this.isStarted = isStarted;
  }

  setIsDisposing(_isDisposing: boolean): void {
    this.isDisposing = true;
  }

  @action
  dispose = async (): Promise<void> => {
    this.isStarted = false;
    this.events.dispose();
  };

  @action
  setIsBusy(isBusy: boolean): void {
    this.isBusy = isBusy;
  }

  @action
  setIsNoAnswer(isNoAnswer: boolean): void {
    this.isNoAnswer = isNoAnswer;
  }

  @action setCallLimits(limits: ICallLimits): void {
    this.callLimits = limits;
  }

  @action
  setInitiatorId = (initiatorId: string): void => {
    this.initiatorId = initiatorId;
  };

  @computed
  get isScreenSharing(): boolean {
    return Boolean(this.opponents.screenSharingOpponent);
  }

  @action
  setMaxBroadcasters(maxBroadcasters: number): void {
    this.maxBroadcasters = maxBroadcasters;
  }

  @action
  setStartTs(startTs: number): void {
    this.startTs = startTs;
  }

  @action setGuestsAllowed(isAllowed: boolean): void {
    this.isGuestsAllowed = isAllowed;
  }

  @action
  setChannelId(channelId: string): void {
    this.channelId = channelId;
  }

  @action
  setAvatar(avatar: ICallAvatar): void {
    this.avatar = avatar;
  }

  @action
  startRecording(recordingId: string): void {
    this.recordingId = recordingId;
  }

  @action
  setGroupId(groupId: string): void {
    this.groupId = groupId;
  }

  @action
  stopRecording(): void {
    this.recordingId = null;
  }

  @action
  setIsCallOwnerOrAdmin(isCallAdmin: boolean): void {
    this.isCallOwnerOrAdmin = isCallAdmin;
  }

  @action
  setProviderUid(uid: ProviderUid): void {
    this.providerUid = uid;
  }

  @action
  setHasVideoPermission(value: CallMediaPermissionValue): void {
    this.videoPermission = value;
  }

  @action
  setHasAudioPermission(value: CallMediaPermissionValue): void {
    this.audioPermission = value;
  }

  @action
  setConnectionQuality(value: CallConnectionQuality): void {
    this.connectionQuality = value;
  }

  @action
  setDuration(duration: number): void {
    this.duration = duration;
  }

  @computed
  get isRecording(): boolean {
    return this.recordingId !== null;
  }

  @computed
  get permissionsRequests(): Map<PermissionsTypes, string[]> {
    return this.permissions.permissionsRequests;
  }

  @computed
  get isPermissionsRequestsEmpty(): boolean {
    return !this.permissions.permissionsRequestsCount;
  }

  @computed
  get isMeInitiatorOwnerOrAdmin(): boolean {
    return this.isCallOwnerOrAdmin || this.isMeInitiator;
  }

  @computed
  get canStartRecording(): boolean {
    return !this.isRecording;
  }

  @computed
  get canStopRecording(): boolean {
    return this.isRecording;
  }

  @action
  setInviteLink(link: string): void {
    this.inviteLink = link;
  }

  @computed
  get isDisabledShareScreen(): boolean {
    return !this.isAnswered;
  }

  @computed
  get isAvailableMuteAudio(): boolean {
    return this.isMeInitiatorOwnerOrAdmin;
  }

  @computed
  get isAvailableRemoveMember(): boolean {
    return this.isMeInitiatorOwnerOrAdmin;
  }

  @computed get canUnmute(): boolean {
    return this.opponents.opponentsPublishers.length < this.maxBroadcasters;
  }

  get p2pOpponent(): Opponent | null {
    // if (this.isP2P) {
    //   const memberDevice = this.members.memberDevicesList.find(
    //     (memberDevice) => memberDevice.isMyDevice
    //   );
    //   if (memberDevice && memberDevice.uid) {
    //     return this.opponents.getOpponent(memberDevice.uid);
    //   }
    // }

    return null;
  }
}
